<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { useGtag } from '@/composition/use-gtag';
  import {
    checklistBands,
    checklistProgress,
    progressPercent,
  } from '@/domain/store-setup/setup-dashboard';
  import { renderTextRef } from '@/domain/store-setup/setup-flow';
  import type { IChecklistRow } from '@/domain/store-setup/setup-flow';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Ponga su tienda a punto» (plan §4.10), styled like its neighbour
   * widgets: the setup checklist of a store created through the flow, with a
   * progress bar, the rows grouped by band («Para recibir pedidos», «Para
   * vender más»), one action per pending row and, once everything is done,
   * «Ocultar lista». Rows come from `buildChecklist`; the page does the
   * navigation and the writes.
   */
  const props = withDefaults(
    defineProps<{
      rows: IChecklistRow[];
      /** A write of the list is running (hide, «Ya lo hice»). */
      busy?: boolean;
    }>(),
    { busy: false }
  );
  const emit = defineEmits<{
    (e: 'hide'): void;
    (e: 'qr-done'): void;
    (e: 'go', to: string): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const gtag = useGtag();
  const titleId = nextDomId('setup-checklist');

  const progress = computed(() => checklistProgress(props.rows));
  const bands = computed(() => checklistBands(props.rows));
  const progressWidth = computed(
    () => `${progressPercent(progress.value.done, progress.value.total)}%`
  );

  function statusText(row: IChecklistRow): string {
    return renderTextRef(
      row.status,
      (key, params, count) =>
        count === undefined
          ? String(proxy.$t(key, params))
          : String(proxy.$tc(key, count, params)),
      proxy.$i18n.locale
    );
  }

  function runAction(row: IChecklistRow) {
    if (!row.action) return;
    gtag.event('checklist_item_clicked', { key: row.key });
    emit('go', row.action.to);
  }

  function runSecondary(row: IChecklistRow) {
    if (!row.secondary || props.busy) return;
    gtag.event('checklist_item_clicked', { key: row.secondary.action });
    emit('qr-done');
  }
</script>

<template>
  <section class="v2-store-setup-checklist" :aria-labelledby="titleId">
    <header class="checklist-head">
      <PosUiIcon
        name="clipboard-check-outline"
        class="head-icon"
        aria-hidden="true"
      />
      <h2 :id="titleId" class="head-title">
        {{
          progress.allDone
            ? $t('storeSetup.checklist.allDone')
            : $t('storeSetup.checklist.title')
        }}
      </h2>
      <PosUiButton
        v-if="progress.allDone"
        class="head-hide"
        color="neutral"
        variant="ghost"
        size="sm"
        :disabled="busy"
        @click="emit('hide')"
        >{{ $t('storeSetup.checklist.hide') }}</PosUiButton
      >
    </header>
    <div class="checklist-progress">
      <div class="progress-track" aria-hidden="true">
        <div
          class="progress-value"
          :class="{ 'is-complete': progress.allDone }"
          :style="{ width: progressWidth }"
        />
      </div>
      <p class="head-progress">
        {{
          $t('storeSetup.checklist.progress', {
            done: progress.done,
            total: progress.total,
          })
        }}
      </p>
    </div>
    <div v-for="group in bands" :key="group.band" class="checklist-band">
      <h3 class="band-title">{{ $t(group.titleKey) }}</h3>
      <ul class="band-rows">
        <li
          v-for="row in group.rows"
          :key="row.key"
          class="checklist-row"
          :class="{ 'is-done': row.done }"
        >
          <PosUiIcon
            class="row-check"
            :name="row.done ? 'check-circle' : 'checkbox-blank-circle-outline'"
            aria-hidden="true"
          />
          <div class="row-copy">
            <p class="row-title">{{ $t(row.titleKey) }}</p>
            <p class="row-status">{{ statusText(row) }}</p>
            <div
              v-if="!row.done && (row.action || row.secondary)"
              class="row-actions"
            >
              <button
                v-if="row.action"
                type="button"
                class="row-action"
                @click="runAction(row)"
              >
                {{ $t(row.action.labelKey) }}
              </button>
              <button
                v-if="row.secondary"
                type="button"
                class="row-link"
                :disabled="busy"
                @click="runSecondary(row)"
              >
                {{ $t(row.secondary.labelKey) }}
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>

<style scoped lang="scss">
  // The surface, header and orange progress bar of the neighbour widgets
  // (the onboarding card it replaces, «Estado de Operaciones»). Spacing uses
  // margins, not flex gap (unsupported on old POS devices, Chrome <84).
  .v2-store-setup-checklist {
    @apply tw-flex tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-neutral-100 tw-bg-white tw-pb-1 tw-shadow-sm;
    // «Ocultar lista» drops to its own line rather than squeezing a long
    // title in the narrow side column.
    .checklist-head {
      @apply tw-flex tw-flex-wrap tw-items-center tw-px-4 tw-pb-2 tw-pt-3;
    }
    .head-icon {
      @apply tw-mr-2 tw-shrink-0 tw-text-xl tw-text-orange-500;
    }
    .head-title {
      @apply tw-m-0 tw-min-w-0 tw-text-base tw-font-semibold tw-text-neutral-800;
      flex: 1 1 6rem;
    }
    // «sm» button in a touch-first admin still gets a 44 px hit area.
    .head-hide {
      @apply tw-ml-auto tw-min-h-11 tw-shrink-0;
    }
    .checklist-progress {
      @apply tw-px-4 tw-pb-1;
    }
    .progress-track {
      @apply tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-orange-100;
    }
    .progress-value {
      @apply tw-h-full tw-rounded-full tw-bg-orange-500 tw-transition-all tw-duration-300;
      &.is-complete {
        @apply tw-bg-green-500;
      }
    }
    .head-progress {
      @apply tw-m-0 tw-mt-1.5 tw-text-xs tw-text-neutral-500;
    }
    .band-title {
      @apply tw-m-0 tw-px-4 tw-pb-1 tw-pt-3 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-neutral-500;
    }
    .band-rows {
      @apply tw-m-0 tw-list-none tw-p-0;
    }
    // The actions sit under the text, so they read the same in the narrow
    // side column and on full-width phones.
    .checklist-row {
      @apply tw-flex tw-items-start tw-px-4 tw-py-2.5;
      & + .checklist-row {
        @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100;
      }
      .row-check {
        @apply tw-mr-3 tw-shrink-0 tw-text-neutral-300;
        font-size: 22px;
      }
      .row-copy {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;
      }
      .row-title {
        @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-800;
      }
      .row-status {
        @apply tw-m-0 tw-text-xs tw-text-neutral-500;
      }
      // 20 px between links and 24 px between wrapped lines, so their hit
      // areas never overlap; the negative margins cancel the outer ones.
      .row-actions {
        @apply tw--mb-6 tw--ml-5 tw-mt-2 tw-flex tw-flex-wrap;
      }
      // Text links: a 44 px tall hit area without growing the row.
      .row-action,
      .row-link {
        @apply tw-relative tw-mb-6 tw-ml-5 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-underline-offset-4;
        &::after {
          @apply tw-absolute tw--inset-x-2 tw--inset-y-3 tw-content-[''];
        }
        &:focus-visible {
          @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
      // Above «Ya lo hice» (which writes at once) if their areas ever meet.
      .row-action {
        @apply tw-z-[1] tw-text-orange-700 hover:tw-underline;
      }
      .row-link {
        @apply tw-text-neutral-600 tw-underline tw-decoration-neutral-300 hover:tw-text-neutral-900 hover:tw-decoration-current;
        &:disabled {
          @apply tw-cursor-not-allowed tw-text-neutral-400 hover:tw-text-neutral-400 hover:tw-decoration-neutral-300;
        }
      }
      &.is-done {
        .row-check {
          @apply tw-text-green-500;
        }
        .row-title {
          @apply tw-text-neutral-500;
        }
      }
    }
  }
</style>
